import React, { useState, useEffect, useMemo } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Switch } from "@/components/ui/switch"
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import { useUpdateDomain } from '@/hooks/useDomains'
import { logBulkOperation, updateBulkOperation } from '@/hooks/useBulkOperations'
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { CalendarIcon } from 'lucide-react'
import { format } from 'date-fns'
import { cn } from "@/lib/utils"
import { EditableDropdown } from '@/components/ui/editable-dropdown'
import { useClientsList } from '@/hooks/useClients'

interface DomainBulkEditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedDomains: string[]
  selectedCount: number
  onUpdate: () => void
  onClearSelection: () => void
  // New props for single domain editing
  singleDomain?: any // Domain object for single edit mode
  isSingleEdit?: boolean // Flag to indicate single edit mode
  // Dynamic options from database
  statusOptions?: string[]
  registrarOptions?: string[]
  mailboxProviderOptions?: string[]
  emailProviderOptions?: string[]
}

interface BulkUpdateData {
  status?: string
  domain_registrar?: string
  mailbox_provider?: string
  email_provider?: string
  auto_renewal?: boolean
  renewal_date?: string
  expiry_date?: string
  notes?: string
  client_name?: string
}

// Domain status options (these are the correct, curated options for domains)
const defaultStatusOptions = ['Active', 'Inactive', 'Expired', 'Suspended', 'Blacklisted', 'Deactivated', 'ORDERED/PURCHASED', 'WAITING FOR UPLOAD']
const defaultRegistrarOptions = ['Hosting.de', 'Cloudflare', 'GoDaddy', 'Namecheap', 'Superwave', 'Primary', 'Spaceship', 'N/A']
const defaultMailboxProviderOptions = ['Superwave', 'Maildoso', 'Cheap Inboxes', 'Primary', 'Google Workspace', 'Microsoft 365', 'No provider', 'N/A']
const defaultEmailProviderOptions = ['Google', 'Microsoft', 'Cloudflare', 'Smtp', 'No provider', 'N/A']

export function DomainBulkEditDialog({
  open,
  onOpenChange,
  selectedDomains,
  selectedCount,
  onUpdate,
  onClearSelection,
  singleDomain,
  isSingleEdit = false,
  statusOptions: propStatusOptions,
  registrarOptions: propRegistrarOptions,
  mailboxProviderOptions: propMailboxProviderOptions,
  emailProviderOptions: propEmailProviderOptions
}: DomainBulkEditDialogProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [updateData, setUpdateData] = useState<BulkUpdateData>({})
  const [renewalDate, setRenewalDate] = useState<Date>()
  const [expiryDate, setExpiryDate] = useState<Date>()

  // Dynamic options state for adding new values
  const [dynamicStatusOptions, setDynamicStatusOptions] = useState<string[]>([])
  const [dynamicRegistrarOptions, setDynamicRegistrarOptions] = useState<string[]>([])
  const [dynamicMailboxProviderOptions, setDynamicMailboxProviderOptions] = useState<string[]>([])
  const [dynamicEmailProviderOptions, setDynamicEmailProviderOptions] = useState<string[]>([])

  const { data: clientsList = [] } = useClientsList()
  const clientOptions = useMemo(() => {
    const unique = new Map<string, { value: string; label: string }>()
    clientsList.forEach(client => {
      if (client?.name) {
        unique.set(client.name, { value: client.name, label: client.name })
      }
    })
    if (singleDomain?.client_name) {
      unique.set(singleDomain.client_name, { value: singleDomain.client_name, label: singleDomain.client_name })
    }
    if (updateData.client_name) {
      unique.set(updateData.client_name, { value: updateData.client_name, label: updateData.client_name })
    }
    return Array.from(unique.values()).sort((a, b) => a.label.localeCompare(b.label))
  }, [clientsList, singleDomain?.client_name, updateData.client_name])

  // Helper to combine default options with unique database values, prioritizing defaults
  const combineOptions = (defaultOpts: string[], propOpts?: string[], dynamicOpts?: string[]) => {
    // Start with default options (the accurate ones)
    const combined = new Set(defaultOpts)
    
    // Add any unique values from database that aren't already in defaults
    if (propOpts) {
      propOpts.forEach(opt => {
        if (opt && opt.trim() && !defaultOpts.includes(opt.trim())) {
          combined.add(opt.trim())
        }
      })
    }
    
    // Add any dynamically added options
    if (dynamicOpts) {
      dynamicOpts.forEach(opt => {
        if (opt && opt.trim()) {
          combined.add(opt.trim())
        }
      })
    }
    
    return Array.from(combined).sort()
  }

  // Use accurate default options as primary, with unique database values as additional
  const finalStatusOptions = combineOptions(defaultStatusOptions, propStatusOptions, dynamicStatusOptions)
  const finalRegistrarOptions = combineOptions(defaultRegistrarOptions, propRegistrarOptions, dynamicRegistrarOptions)
  const finalMailboxProviderOptions = combineOptions(defaultMailboxProviderOptions, propMailboxProviderOptions, dynamicMailboxProviderOptions)
  const finalEmailProviderOptions = combineOptions(defaultEmailProviderOptions, propEmailProviderOptions, dynamicEmailProviderOptions)

  // Helper function to add new options
  const addNewOption = (optionType: 'status' | 'registrar' | 'mailbox_provider' | 'email_provider', newValue: string) => {
    switch (optionType) {
      case 'status':
        if (!finalStatusOptions.includes(newValue)) {
          setDynamicStatusOptions(prev => [...prev, newValue])
        }
        break
      case 'registrar':
        if (!finalRegistrarOptions.includes(newValue)) {
          setDynamicRegistrarOptions(prev => [...prev, newValue])
        }
        break
      case 'mailbox_provider':
        if (!finalMailboxProviderOptions.includes(newValue)) {
          setDynamicMailboxProviderOptions(prev => [...prev, newValue])
        }
        break
      case 'email_provider':
        if (!finalEmailProviderOptions.includes(newValue)) {
          setDynamicEmailProviderOptions(prev => [...prev, newValue])
        }
        break
    }
  }

  // Initialize form data for single domain editing
  useEffect(() => {
    if (isSingleEdit && singleDomain) {
      setUpdateData({
        status: singleDomain.status || '',
        domain_registrar: singleDomain.domain_registrar || '',
        mailbox_provider: singleDomain.mailbox_provider || '',
        email_provider: singleDomain.email_provider || '',
        auto_renewal: singleDomain.auto_renewal || false,
        notes: singleDomain.notes || '',
        client_name: singleDomain.client_name || '',
      })
      
      // Set dates if they exist
      if (singleDomain.renewal_date) {
        setRenewalDate(new Date(singleDomain.renewal_date))
      }
      if (singleDomain.expiry_date) {
        setExpiryDate(new Date(singleDomain.expiry_date))
      }
    } else {
      // Reset form for bulk edit
      setUpdateData({})
      setRenewalDate(undefined)
      setExpiryDate(undefined)
    }
  }, [isSingleEdit, singleDomain, open])

  const handleInputChange = (field: keyof BulkUpdateData, value: string | boolean) => {
    setUpdateData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSingleUpdate = async () => {
    if (!singleDomain) return

    setIsLoading(true)
    
    try {
      // Prepare final update data including dates
      const finalUpdateData = { ...updateData }
      if (renewalDate) {
        finalUpdateData.renewal_date = format(renewalDate, 'yyyy-MM-dd')
      }
      if (expiryDate) {
        finalUpdateData.expiry_date = format(expiryDate, 'yyyy-MM-dd')
      }

      // Update the single domain
      const { error } = await supabase
        .from('domains')
        .update(finalUpdateData)
        .eq('id', singleDomain.id)

      if (error) {
        throw error
      }

      toast.success('Domain updated successfully', {
        duration: 2000,
        closeButton: true
      })

      onUpdate()
      onOpenChange(false)
    } catch (error) {
      console.error('Single domain update failed:', error)
      toast.error('Failed to update domain', {
        duration: 2000,
        closeButton: true
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleBulkUpdate = async () => {
    // For single edit, we update all fields regardless
    if (isSingleEdit) {
      await handleSingleUpdate()
      return
    }

    // For bulk edit, only update fields with values
    const fieldsToUpdate = Object.keys(updateData).filter(key => 
      updateData[key as keyof BulkUpdateData] !== undefined && 
      updateData[key as keyof BulkUpdateData] !== ''
    )

    if (fieldsToUpdate.length === 0 && !renewalDate && !expiryDate) {
      toast.error('Please select at least one field to update', {
        duration: 2000,
        closeButton: true
      })
      return
    }

    setIsLoading(true)
    let operationId: string | null = null
    
    try {
      // Prepare final update data including dates
      const finalUpdateData = { ...updateData }
      if (renewalDate) {
        finalUpdateData.renewal_date = format(renewalDate, 'yyyy-MM-dd')
      }
      if (expiryDate) {
        finalUpdateData.expiry_date = format(expiryDate, 'yyyy-MM-dd')
      }

      // Log the operation start
      const operation = await logBulkOperation(
        'domain_bulk_edit',
        selectedCount,
        finalUpdateData,
        'User'
      )
      operationId = operation.id
      
      // Close modal immediately and run in background
      onOpenChange(false)
      setUpdateData({})
      setRenewalDate(undefined)
      setExpiryDate(undefined)
      
      // Show initial toast
      const toastId = toast.loading(`Starting bulk update for ${selectedCount} domains...`, {
        description: "Processing domains in background",
        duration: Infinity
      })
      
      let successful = 0
      let failed = 0
      const errors: string[] = []
      
      // Process domains
      for (let i = 0; i < selectedDomains.length; i++) {
        const domainId = selectedDomains[i]
        const current = i + 1
        
        // Update toast with progress
        toast.loading(`Processing domains: ${current}/${selectedCount}`, {
          id: toastId,
          description: `Success: ${successful} | Failed: ${failed}`,
          duration: Infinity
        })
        
        try {
          // Get domain details first
          const { data: domainData, error: fetchError } = await supabase
            .from('domains')
            .select('*')
            .eq('id', domainId)
            .single()

          if (fetchError || !domainData) {
            throw new Error(`Domain not found: ${fetchError?.message}`)
          }

          // Update each field individually
          for (const [field, value] of Object.entries(finalUpdateData)) {
            if (value !== undefined && value !== '') {
              const { error: updateError } = await supabase
                .from('domains')
                .update({ 
                  [field]: value,
                  updated_at: new Date().toISOString()
                })
                .eq('id', domainId)

              if (updateError) {
                throw updateError
              }
            }
          }

          successful++
        } catch (error) {
          console.error(`Failed to update domain ${domainId}:`, error)
          failed++
          const domainName = selectedDomains[i] || domainId
          
          let simpleError = 'Unknown error'
          if (error instanceof Error) {
            simpleError = error.message
          }
          
          errors.push(`${domainName}: ${simpleError}`)
        }
        
        // Small delay to prevent overwhelming the database
        if (i < selectedDomains.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      }

      // Update operation with final results
      if (operationId) {
        await updateBulkOperation(operationId, {
          successful_count: successful,
          failed_count: failed,
          errors: errors,
          completed_at: new Date().toISOString()
        })
      }

      // Final result toast
      toast.dismiss(toastId)
      if (failed === 0) {
        toast.success(`Successfully updated all ${selectedCount} domains`, {
          duration: 2000,
          closeButton: true
        })
      } else {
        toast.warning(`Updated ${successful} domains, ${failed} failed`, {
          duration: 2000,
          closeButton: true
        })
      }

      onUpdate()
      onClearSelection()
    } catch (error) {
      console.error('Bulk update failed:', error)
      
      // Update operation as failed
      if (operationId) {
        await updateBulkOperation(operationId, {
          successful_count: 0,
          failed_count: selectedCount,
          errors: [error instanceof Error ? error.message : 'Unknown error'],
        })
      }
      
      toast.error('Failed to update domains', {
        duration: 2000,
        closeButton: true
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleBulkDelete = async () => {
    if (!confirm(`Are you sure you want to delete ${selectedCount} domains? This action cannot be undone.`)) {
      return
    }

    setIsLoading(true)
    try {
      const { error } = await supabase
        .from('domains')
        .delete()
        .in('id', selectedDomains)

      if (error) throw error

      toast.success(`Deleted ${selectedCount} domains`, {
        duration: 2000, // 2 seconds instead of default 4 seconds
        closeButton: true
      })
      onUpdate()
      onClearSelection()
      onOpenChange(false)
    } catch (error) {
      toast.error('Failed to delete domains', {
        duration: 2000,
        closeButton: true
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isSingleEdit ? `Edit Domain - ${singleDomain?.domain}` : `Bulk Update - ${selectedCount} Domains`}
          </DialogTitle>
          <DialogDescription>
            {isSingleEdit 
              ? "Update domain properties. All fields will be saved."
              : "Update multiple domain properties at once. Only fill in the fields you want to change."
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-4">
            <div className="grid grid-cols-3 items-center gap-4">
              <Label htmlFor="status" className="text-right">Status</Label>
              <div className="col-span-2">
                <EditableDropdown
                  value={updateData.status || ''}
                  onChange={(value) => {
                    addNewOption('status', value)
                    handleInputChange('status', value)
                  }}
                  options={finalStatusOptions}
                  placeholder="Select status"
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 items-center gap-4">
              <Label htmlFor="client_name" className="text-right">Client</Label>
              <div className="col-span-2">
                <EditableDropdown
                  value={updateData.client_name || ''}
                  onChange={(value) => handleInputChange('client_name', value)}
                  options={clientOptions}
                  placeholder="Select or add client"
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 items-center gap-4">
              <Label htmlFor="domain_registrar" className="text-right">Domain Registrar</Label>
              <div className="col-span-2">
                <EditableDropdown
                  value={updateData.domain_registrar || ''}
                  onChange={(value) => {
                    addNewOption('registrar', value)
                    handleInputChange('domain_registrar', value)
                  }}
                  options={finalRegistrarOptions}
                  placeholder="Select or type registrar"
                  className="w-full"
                  allowCreate={true}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 items-center gap-4">
              <Label htmlFor="mailbox_provider" className="text-right">Mailbox Provider</Label>
              <div className="col-span-2">
                <EditableDropdown
                  value={updateData.mailbox_provider || ''}
                  onChange={(value) => {
                    addNewOption('mailbox_provider', value)
                    handleInputChange('mailbox_provider', value)
                  }}
                  options={finalMailboxProviderOptions}
                  placeholder="Select provider"
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 items-center gap-4">
              <Label htmlFor="email_provider" className="text-right">Email Provider</Label>
              <div className="col-span-2">
                <EditableDropdown
                  value={updateData.email_provider || ''}
                  onChange={(value) => {
                    addNewOption('email_provider', value)
                    handleInputChange('email_provider', value)
                  }}
                  options={finalEmailProviderOptions}
                  placeholder="Select provider"
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 items-center gap-4">
              <Label htmlFor="renewal_date" className="text-right">Renewal Date</Label>
              <div className="col-span-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !renewalDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {renewalDate ? format(renewalDate, "dd/MM/yyyy") : "DD/MM/YYYY"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={renewalDate}
                      onSelect={setRenewalDate}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid grid-cols-3 items-center gap-4">
              <Label htmlFor="expiry_date" className="text-right">Expiry Date</Label>
              <div className="col-span-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !expiryDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {expiryDate ? format(expiryDate, "dd/MM/yyyy") : "DD/MM/YYYY"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={expiryDate}
                      onSelect={setExpiryDate}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid grid-cols-3 items-start gap-4">
              <Label htmlFor="auto_renewal" className="text-right pt-2">Auto Renewal</Label>
              <div className="col-span-2">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="auto_renewal"
                    onCheckedChange={(checked) => handleInputChange('auto_renewal', checked)}
                  />
                  <Label htmlFor="auto_renewal" className="text-sm text-muted-foreground">
                    Enable automatic renewal
                  </Label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 items-start gap-4">
              <Label htmlFor="notes" className="text-right pt-2">Notes</Label>
              <div className="col-span-2">
                <textarea
                  id="notes"
                  placeholder="Add notes about these domains..."
                  className="w-full min-h-[80px] px-3 py-2 text-sm border border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 rounded-md resize-none"
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                />
              </div>
            </div>
          </div>

          {!isSingleEdit && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">How this works:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Only fields with values will be updated</li>
                <li>• All selected domains will receive the same updates</li>
                <li>• Operation is logged for audit purposes</li>
                <li>• Process runs sequentially to ensure data consistency</li>
              </ul>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          {!isSingleEdit && (
            <Button variant="destructive" onClick={handleBulkDelete} disabled={isLoading}>
              Delete {selectedCount} Domains
            </Button>
          )}
          <Button onClick={handleBulkUpdate} disabled={isLoading}>
            {isLoading ? 'Updating...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}